import _, { isEqual, isObject } from 'lodash';
import {
  MyLeadsSearchQuery,
  MyLeadsFilterTypes,
  myLeadsAttributesBasedTypes,
} from '@mm/state/interfaces/myLeads';
import { FilterItem, TagFilterItem } from '@mm/state/model/AudiencesModel';
import { getUniqId } from '@mm/utils';
import { convertToUpperSnakeCase } from '../../GetLeads/LeadsSearch/LeadsSearchFiltersMenu/helpers';
import { MAX_QUERY_ATTRIBUTES_LENGTH } from '../../GetLeads/LeadsSearch/LeadsSearchFiltersMenu/LeadsSearchFiltersMenu';

export type UpdateFilterParamsType = {
  key: string | number;
  value: string | Record<string, any> | boolean | string[];
  isExisting: boolean;
  type: MyLeadsFilterTypes;
  skipPrevValues?: boolean;
};

export const useFilterParams = ({
  filterParams,
}: {
  filterParams: MyLeadsSearchQuery;
}) => {
  const isFilterParamExist = ({
    key,
    value,
    type = MyLeadsFilterTypes.variables,
  }: {
    key: string;
    value: string | boolean;
    type: MyLeadsFilterTypes;
  }): boolean => {
    const searchKey = myLeadsAttributesBasedTypes.includes(type)
      ? key
      : convertToUpperSnakeCase(key);
    const arr = filterParams.query?.[type]?.[searchKey];
    const isObjectValue = isObject(arr?.[0]);

    const valuesArr: string[] = isObjectValue
      ? Object.values(arr?.[0] || {}).flat()
      : arr || [];

    return !!valuesArr && valuesArr.some(val => val === value);
  };

  const hasActiveFilters = ({
    keys,
    types,
  }: {
    keys: string[];
    types: MyLeadsFilterTypes[];
  }): boolean =>
    types.some(type => {
      const checkedKeys = myLeadsAttributesBasedTypes.includes(type)
        ? keys
        : keys.map(key => convertToUpperSnakeCase(key));
      const obj = filterParams?.query?.[type] || {};

      return checkedKeys.some(key =>
        Object.prototype.hasOwnProperty.call(obj, key),
      );
    });

  const getUpdatedFilterParams = ({
    key,
    value,
    isExisting,
    type = MyLeadsFilterTypes.variables,
    skipPrevValues = false,
  }: UpdateFilterParamsType): MyLeadsSearchQuery => {
    const {
      query,
      filterParams: oldFilterParams,
      ...rest
    } = filterParams || {};

    const paramsKey = myLeadsAttributesBasedTypes.includes(type)
      ? key
      : convertToUpperSnakeCase(key.toString());
    const obj = query?.[type] || {};

    const prevValues = !skipPrevValues && obj[paramsKey] ? obj[paramsKey] : [];
    let values: string[] | Record<string, any> = [...prevValues];
    if (isExisting) {
      values = values.filter(val =>
        Array.isArray(value) ? value.forEach(v => val !== v) : val !== value,
      );
    } else if (value) {
      if (Array.isArray(value)) {
        values.push(...value);
      } else {
        values.push(value);
      }
    }
    const updatedQuery = {
      query: {
        ...filterParams?.query,
        [type]: {
          ...obj,
          [paramsKey]: values,
        },
      },
      filterParams: oldFilterParams?.filter((f: FilterItem) =>
        typeof key === 'number'
          ? f.variableId !== Number(key)
          : f.filterType !== key,
      ),
      ...rest,
    };

    // Remove empty nested objects/arrays if this is the last attribute or variable
    const queryOption = updatedQuery.query[type];
    if (
      queryOption &&
      (Object.keys(queryOption[paramsKey]).length === 0 ||
        (Array.isArray(queryOption[paramsKey]) &&
          queryOption[paramsKey].length === 0))
    ) {
      delete queryOption[paramsKey];
      if (Object.keys(queryOption).length === 0) {
        delete updatedQuery.query[type];
      }
    }

    return updatedQuery;
  };

  const getUpdatedVariablesFilterParams = (
    accessor: 'variableId' | 'filterType',
    filter: FilterItem | Pick<FilterItem, 'filterType' | 'value'>,
  ) => {
    const filterItemsArray: FilterItem[] = [];
    filterParams.filterParams
      ?.filter((f: FilterItem) => f[accessor] !== filter?.[accessor])
      .forEach((f: FilterItem) => filterItemsArray.push(f));

    const uniqId = getUniqId('filter');
    const newFilter = filter.value?.length ? { ...filter, uniqId } : undefined;

    const updatedFilterParams = {
      ...filterParams,
      filterParams: [...filterItemsArray, newFilter].filter(Boolean),
    };

    return updatedFilterParams;
  };

  const getUpdatedTagFilters = (values: string[]) => {
    const tagsFilters = filterParams.tagsFilters as TagFilterItem[];
    const updatedFilterParams = {
      ...filterParams,
      tagsFilters: tagsFilters?.filter(
        (filter: TagFilterItem) =>
          !isEqual(
            filter.values.map(v => v.name),
            values,
          ),
      ),
    };

    return updatedFilterParams;
  };

  const countQueryObjLength = () => {
    let totalCount = 0;
    for (const key in filterParams.query) {
      if (
        Object.prototype.hasOwnProperty.call(filterParams.query, key) &&
        filterParams.query[key] instanceof Object
      ) {
        totalCount += Object.keys(filterParams.query[key] || {}).length;
      }
    }
    return totalCount;
  };

  const filtersCount =
    countQueryObjLength() +
    (filterParams.filterParams?.length || 0) +
    (filterParams?.tagsFilters?.length || 0);
  const hasFilters = filtersCount > 0;

  const shouldDisableFilters = filtersCount >= MAX_QUERY_ATTRIBUTES_LENGTH;

  return {
    isFilterParamExist,
    hasActiveFilters,
    getUpdatedFilterParams,
    getUpdatedVariablesFilterParams,
    getUpdatedTagFilters,
    shouldDisableFilters,
    hasFilters,
    filtersCount,
  };
};
